import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RotateCcw,
  Image as ImageIcon,
  Video as VideoIcon,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CFG = {
  pending: { label: "En cola", icon: Clock, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30", badge: "bg-amber-500/20 text-amber-200" },
  sent: { label: "Enviado", icon: CheckCircle2, color: "text-green-300", bg: "bg-green-500/10", border: "border-green-500/30", badge: "bg-green-500/20 text-green-300" },
  failed: { label: "Rechazado", icon: XCircle, color: "text-red-300", bg: "bg-red-500/10", border: "border-red-500/30", badge: "bg-red-500/20 text-red-300" },
  expired: { label: "Expirado", icon: AlertTriangle, color: "text-orange-300", bg: "bg-orange-500/10", border: "border-orange-500/30", badge: "bg-orange-500/20 text-orange-200" },
  cancelled: { label: "Cancelado", icon: XCircle, color: "text-white/40", bg: "bg-white/5", border: "border-white/10", badge: "bg-white/10 text-white/60" },
};

const PostRow = ({ post, getAuthHeaders, onChange }) => {
  const cfg = STATUS_CFG[post.status] || STATUS_CFG.pending;
  const Icon = cfg.icon;
  const time = new Date(post.scheduledFor).toLocaleString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const retry = async () => {
    const r = await fetch(`/api/admin/autopost/queue/${post.id}/retry`, {
      method: "POST",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) {
      toast.success("🔄 Re-encolado para reintentar");
      onChange && onChange();
    } else {
      toast.error("Error al reintentar");
    }
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 ${cfg.bg} border ${cfg.border} rounded-lg`}
      data-testid={`queue-post-${post.id}`}
    >
      {/* Media thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/40 flex-shrink-0 relative">
        {post.mediaType === "video" ? (
          <video src={post.mediaUrl} className="w-full h-full object-cover" muted />
        ) : (
          <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
        )}
        {post.mediaType === "video" && (
          <VideoIcon className="w-3 h-3 absolute top-0.5 right-0.5 text-white/80" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-white font-medium text-xs">
            @{post.creatorUsername}
          </span>
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
              post.tier === "vip"
                ? "bg-amber-500/20 text-amber-200"
                : "bg-blue-500/20 text-blue-200"
            }`}
          >
            {post.tier.toUpperCase()}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.badge}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
          </span>
          {post.broadcastTier && (
            <span className="text-[10px] bg-purple-500/20 text-purple-200 px-1.5 py-0.5 rounded">
              Auto
            </span>
          )}
        </div>
        <p className="text-white/80 text-xs line-clamp-2">
          {post.caption || <em className="text-white/30">Sin caption</em>}
        </p>
        <div className="flex items-center gap-2 text-[10px] text-white/40 mt-0.5">
          <span>{time}</span>
          <span className="font-mono truncate">{post.channelId}</span>
        </div>
        {post.errorMsg && (
          <p className="text-red-300/80 text-[11px] mt-1 italic">
            ↳ {post.errorMsg}
          </p>
        )}
      </div>

      {(post.status === "failed" || post.status === "expired") && (
        <button
          onClick={retry}
          data-testid={`queue-retry-${post.id}`}
          title="Reintentar envío"
          className="p-1.5 text-white/60 hover:text-white rounded hover:bg-white/10 flex-shrink-0"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const StatusFilter = ({ status, count, color, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`queue-filter-${status || "all"}`}
    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
      active
        ? "bg-white text-black"
        : `bg-${color}/10 text-${color === "white" ? "white" : color.replace("-500", "-300")} border border-${color}/20 hover:bg-${color}/20`
    }`}
  >
    {STATUS_CFG[status]?.label || "Todos"}
    {count != null && (
      <span className="ml-1.5 text-[10px] font-bold opacity-70">{count}</span>
    )}
  </button>
);

const AdminAutoPostQueue = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ posts: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (tierFilter) params.set("tier", tierFilter);
      params.set("days", String(days));
      const r = await fetch(`/api/admin/autopost/queue-global?${params}`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible aún. Haz Deploy en Emergent.");
        return;
      }
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error("Error al cargar la cola");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, tierFilter, days]);

  useEffect(() => {
    load();
  }, [load]);

  const c = data.counts || {};

  return (
    <AdminLayout>
      <div className="space-y-5">
        <Link
          to="/admin/auto-posting"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Canales
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              📊 Cola de envíos
            </h1>
            <p className="text-white/60 text-sm">
              Estado en tiempo real de todo el auto-posting (últimos {days} días)
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              data-testid="queue-days-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
            >
              <option value="1">Último día</option>
              <option value="7">7 días</option>
              <option value="30">30 días</option>
              <option value="90">90 días</option>
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 disabled:opacity-50"
              data-testid="queue-reload"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <button
            onClick={() => setStatusFilter("")}
            className={`text-left bg-white/5 border ${
              !statusFilter ? "border-white/30" : "border-white/10"
            } rounded-lg p-3 hover:border-white/20`}
          >
            <div className="text-white/60 text-xs">Total</div>
            <div className="text-white text-2xl font-bold">{c.total || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter("sent")}
            className={`text-left bg-green-500/10 border ${
              statusFilter === "sent" ? "border-green-400" : "border-green-500/20"
            } rounded-lg p-3 hover:border-green-400/60`}
          >
            <div className="text-green-300 text-xs flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Enviados
            </div>
            <div className="text-white text-2xl font-bold">{c.sent || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter("pending")}
            className={`text-left bg-amber-500/10 border ${
              statusFilter === "pending" ? "border-amber-400" : "border-amber-500/20"
            } rounded-lg p-3 hover:border-amber-400/60`}
          >
            <div className="text-amber-300 text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" /> En cola
            </div>
            <div className="text-white text-2xl font-bold">{c.pending || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter("failed")}
            className={`text-left bg-red-500/10 border ${
              statusFilter === "failed" ? "border-red-400" : "border-red-500/20"
            } rounded-lg p-3 hover:border-red-400/60`}
          >
            <div className="text-red-300 text-xs flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Rechazados
            </div>
            <div className="text-white text-2xl font-bold">{c.failed || 0}</div>
          </button>
          <button
            onClick={() => setStatusFilter("expired")}
            className={`text-left bg-orange-500/10 border ${
              statusFilter === "expired" ? "border-orange-400" : "border-orange-500/20"
            } rounded-lg p-3 hover:border-orange-400/60`}
          >
            <div className="text-orange-300 text-xs flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Expirados
            </div>
            <div className="text-white text-2xl font-bold">{c.expired || 0}</div>
          </button>
        </div>

        {/* Tier filter */}
        <div className="flex gap-2 items-center">
          <span className="text-white/50 text-xs">Tier:</span>
          {[
            { value: "", label: "Ambos" },
            { value: "free", label: "Free" },
            { value: "vip", label: "VIP" },
          ].map((t) => (
            <button
              key={t.value}
              onClick={() => setTierFilter(t.value)}
              data-testid={`queue-tier-${t.value || "all"}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                tierFilter === t.value
                  ? "bg-white text-black"
                  : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
          {(statusFilter || tierFilter) && (
            <button
              onClick={() => {
                setStatusFilter("");
                setTierFilter("");
              }}
              className="ml-auto text-white/60 hover:text-white text-xs underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {/* Posts list */}
        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : (data.posts || []).length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-white/50 text-sm">
              {c.total === 0
                ? "No hay actividad en este rango de tiempo"
                : "Sin posts con esos filtros"}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.posts.map((p) => (
              <PostRow
                key={p.id}
                post={p}
                getAuthHeaders={getAuthHeaders}
                onChange={load}
              />
            ))}
            {data.posts.length >= 200 && (
              <p className="text-center text-white/40 text-xs pt-2">
                Mostrando 200 más recientes — usa filtros para ver más.
              </p>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAutoPostQueue;
